import { errorMessageFactory } from 'factories/api';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { startAppListening } from 'redux/middleware/listenerMiddleware';
import { fetchClients } from 'redux/modules/clients.actions';
import { createProjectDraft } from 'redux/modules/projects.actions';
import { clearDraft, projectSaved } from 'redux/modules/projects.types';
import { showToast } from 'redux/modules/toasts.actions';
import { getActiveClients } from 'redux/selectors/clients';
import { getDraft } from 'redux/selectors/projects';
import { getUserPermissions } from 'redux/selectors/user';
import type { ThunkDispatch } from 'types/redux';
import { isType } from 'typescript-fsa';
import type { ProjectCreation } from './AgentProjectCreateForm';
import { createAgentProject } from './agentProjectCreation';

export function useAgentProjectCreation(
  onAssign: (id: number | null) => Promise<void>
): ProjectCreation | undefined {
  const dispatch = useDispatch<ThunkDispatch>();
  const allowed = useSelector(getUserPermissions).canCreate.projects;
  const stop = useRef<(() => void) | undefined>(undefined);
  useEffect(() => () => stop.current?.(), []);
  if (!allowed) return undefined;
  return {
    loadClients: () =>
      dispatch(async (dispatch, getState) => {
        await dispatch(fetchClients());
        return getActiveClients(getState());
      }),
    create: async (name, clientId, color) => {
      try {
        return await dispatch(createAgentProject(name, clientId, color));
      } catch (error) {
        throw new Error(errorMessageFactory(error));
      }
    },
    openFull: (name, client, color) => {
      stop.current?.();
      dispatch(createProjectDraft({ name, color, ...(client ? { client } : {}) }));
      const opened = dispatch((_dispatch, getState) => !!getDraft(getState()));
      if (!opened) return;
      stop.current = startAppListening({
        predicate: action => isType(action, projectSaved) || isType(action, clearDraft),
        effect: async action => {
          if (isType(action, clearDraft)) {
            stop.current?.();
            return;
          }
          if (!isType(action, projectSaved) || !action.payload.createdEntry) return;
          stop.current?.();
          try {
            await onAssign(action.payload.project.id);
          } catch {
            dispatch(
              showToast({
                type: 'danger',
                message:
                  'Project created, but could not be assigned. Select it from the project picker to try again.',
              })
            );
          }
        },
      });
    },
  };
}
